import { createFileRoute } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, Clock, Copy, Globe, Plus, RefreshCw, ShieldCheck, Trash2 } from "lucide-react";
import { EmptyState, FormSkeleton, PageSkeleton, toast } from "@bs/ui";
import type { CustomDomainItem } from "@bs/contracts";
import { Button } from "@bs/ui";
import { Checkbox } from "@bs/ui";
import { Input } from "@bs/ui";
import { ConfirmDialog } from "@bs/ui";
import { Field } from "@bs/ui";
import { HeaderActions, SettingsCard, SettingsPageFrame } from "../../../components/settings/settings-page.tsx";
import { orpc } from "../../../lib/orpc.ts";
import { errorMessage } from "../../../lib/errors.ts";
import { describeRouting, isApexDomain } from "../../../lib/domain-hosts.ts";

export const Route = createFileRoute("/_store/settings/domains")({
  pendingComponent: () => <PageSkeleton />,
  component: DomainsSettingsPage,
});

const TITLE = "Domains";
const DESCRIPTION = "Connect and manage your custom domain so shoppers find your store at your own address.";

const FQDN_PATTERN = /^(?!:\/\/)([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/;

/** Plain-language steps shown before and while a domain is added, so owners know what to create at their DNS provider. */
function DomainSetupGuide() {
  return (
    <div className="rounded-md border border-border bg-muted/30 p-3.5 text-xs" aria-label="How to connect a domain">
      <p className="font-medium text-foreground">How connecting a domain works</p>
      <ol className="mt-2 list-decimal space-y-1.5 pl-4 text-muted-foreground">
        <li>
          <span className="font-medium text-foreground">Add your domain here.</span> We recommend a <span className="font-mono">www</span> address such as{" "}
          <span className="font-mono">www.mybrand.com</span>: it needs one simple record and works with every DNS provider.
        </li>
        <li>
          <span className="font-medium text-foreground">Create the DNS record we show you</span> at your domain provider (GoDaddy, Hostinger, Namecheap,
          Cloudflare and so on). The optional ownership check needs two extra TXT records, but it is{" "}
          <span className="font-medium text-foreground">not enough on its own</span>: your domain must also point to us.
        </li>
        <li>
          <span className="font-medium text-foreground">Wait for DNS to update.</span> This is usually a few minutes but can take up to 24 to 48 hours.
        </li>
        <li>
          <span className="font-medium text-foreground">Press "Check status".</span> We look at your domain's real DNS and open the address ourselves. It shows
          as Active only when visitors really reach your store, and then you can make it your primary address.
        </li>
      </ol>
      <p className="mt-2 text-muted-foreground">
        <span className="font-medium text-foreground">Bare domain (advanced)?</span> For <span className="font-mono">mybrand.com</span> without{" "}
        <span className="font-mono">www</span>, DNS does not allow a CNAME, so we use A records instead, and the order matters: add only the TXT records first, wait until we confirm ownership,
        and only then add the A records. If the A records are already there, remove them until ownership is confirmed. Remove any old A, AAAA or CNAME record on the same
        name first (a parking page record will block it). Many providers can also forward the bare domain to <span className="font-mono">www</span>.
      </p>
    </div>
  );
}

type RecordRow = { type: string; name: string; value: string; copyLabel: string; copyButton: string };

/** One DNS record the merchant must create, with a copy button for the value. */
function RecordLine({ row, onCopy }: { row: RecordRow; onCopy: (text: string, label: string) => void }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 rounded bg-background p-2.5 border border-border">
      <div className="flex flex-wrap items-center gap-3">
        <span className="font-bold text-foreground">{row.type}</span>
        <span className="text-muted-foreground">Name:</span>
        <span className="text-foreground">{row.name}</span>
        <span className="text-muted-foreground">{row.type === "CNAME" ? "Target:" : row.type === "TXT" ? "Value:" : "Points to:"}</span>
        <span className="text-foreground truncate max-w-xs">{row.value}</span>
      </div>
      <Button type="button" size="sm" variant="ghost" onClick={() => onCopy(row.value, row.copyLabel)}>
        <Copy className="size-3.5 mr-1" aria-hidden />
        {row.copyButton}
      </Button>
    </div>
  );
}

export function DomainsSettingsPage() {
  const queryClient = useQueryClient();
  const domainsQuery = useQuery({
    ...orpc.admin.domains.list.queryOptions(),
    // While a domain waits for DNS, quietly re-read the list so a status change made elsewhere (for example by the
    // platform's background check) shows up without a page reload. This never triggers a DNS lookup by itself.
    refetchInterval: (query) =>
      (query.state.data ?? []).some((d) => d.status !== "active" && d.status !== "failed") ? 30_000 : false,
  });

  const addDomain = useMutation(orpc.admin.domains.add.mutationOptions());
  const verifyDomain = useMutation(orpc.admin.domains.verify.mutationOptions());
  const setPrimary = useMutation(orpc.admin.domains.setPrimary.mutationOptions());
  const removeDomain = useMutation(orpc.admin.domains.remove.mutationOptions());

  const [isAdding, setIsAdding] = useState(false);
  const [hostname, setHostname] = useState("");
  const typedApex = isApexDomain(hostname.trim().toLowerCase().replace(/^https?:\/\//, "").replace(/\/.*$/, "")) && hostname.trim().includes(".");
  const [prevalidateTxt, setPrevalidateTxt] = useState(false);
  const [domainToRemove, setDomainToRemove] = useState<CustomDomainItem | null>(null);
  const [verifyingId, setVerifyingId] = useState<string | null>(null);

  function refresh() {
    void queryClient.invalidateQueries({ queryKey: orpc.admin.domains.key() });
  }

  function handleAdd(e: FormEvent) {
    e.preventDefault();
    const clean = hostname.trim().toLowerCase().replace(/^https?:\/\//, "").replace(/\/.*$/, "");
    if (!FQDN_PATTERN.test(clean)) {
      toast.error("Please enter a valid domain name (e.g. store.mydomain.com or mydomain.com).");
      return;
    }

    addDomain.mutate(
      { hostname: clean, prevalidateTxt: prevalidateTxt || isApexDomain(clean) },
      {
        onSuccess: () => {
          setHostname("");
          setPrevalidateTxt(false);
          setIsAdding(false);
          refresh();
          toast.success("Domain added. Please configure DNS records.");
        },
        onError: (err) => toast.error(errorMessage(err)),
      },
    );
  }

  function handleVerify(id: string) {
    setVerifyingId(id);
    verifyDomain.mutate(
      { id },
      {
        onSuccess: (res) => {
          refresh();
          if (res.status === "active") {
            toast.success("Domain verified and active!");
          } else {
            const what = res.routing ? describeRouting(res.hostname, res.routing).text : null;
            toast.info(what ?? `Verification status: ${res.status.replace("_", " ")}. DNS changes may take some time to propagate.`);
          }
          setVerifyingId(null);
        },
        onError: (err) => {
          setVerifyingId(null);
          toast.error(errorMessage(err));
        },
      },
    );
  }

  function handleSetPrimary(id: string) {
    setPrimary.mutate(
      { id },
      {
        onSuccess: () => {
          refresh();
          toast.success("Primary domain updated.");
        },
        onError: (err) => toast.error(errorMessage(err)),
      },
    );
  }

  function handleRemove() {
    if (!domainToRemove) return;
    removeDomain.mutate(
      { id: domainToRemove.id },
      {
        onSuccess: () => {
          setDomainToRemove(null);
          refresh();
          toast.success("Domain disconnected.");
        },
        onError: (err) => toast.error(errorMessage(err)),
      },
    );
  }

  function copy(text: string, label: string) {
    if (typeof navigator !== "undefined" && navigator.clipboard) {
      navigator.clipboard.writeText(text).then(
        () => toast.success(`Copied ${label} to clipboard`),
        () => toast.error(`Could not copy ${label}`),
      );
    }
  }

  if (domainsQuery.isLoading) {
    return (
      <SettingsPageFrame title={TITLE} description={DESCRIPTION}>
        <SettingsCard>
          <FormSkeleton />
        </SettingsCard>
      </SettingsPageFrame>
    );
  }

  if (domainsQuery.isError || !domainsQuery.data) {
    return (
      <SettingsPageFrame title={TITLE} description={DESCRIPTION}>
        <SettingsCard>
          <EmptyState
            icon={AlertTriangle}
            title="Could not load domains"
            description={errorMessage(domainsQuery.error)}
            action={<Button onClick={() => void domainsQuery.refetch()}>Try again</Button>}
          />
        </SettingsCard>
      </SettingsPageFrame>
    );
  }

  const domains = domainsQuery.data;

  return (
    <SettingsPageFrame title={TITLE} description={DESCRIPTION}>
      <HeaderActions>
        {!isAdding && (
          <Button onClick={() => setIsAdding(true)}>
            <Plus className="mr-1.5 size-4" aria-hidden />
            Add custom domain
          </Button>
        )}
      </HeaderActions>

      <ConfirmDialog
        open={Boolean(domainToRemove)}
        onOpenChange={(open) => {
          if (!open) setDomainToRemove(null);
        }}
        title="Remove domain?"
        description={
          domainToRemove ? (
            <span>
              Are you sure you want to disconnect <strong>{domainToRemove.hostname}</strong>? Shoppers will no longer be able to reach your store at this domain.
            </span>
          ) : (
            ""
          )
        }
        confirmLabel="Remove domain"
        destructive
        pending={removeDomain.isPending}
        onConfirm={handleRemove}
      />

      {isAdding && (
        <SettingsCard title="Add custom domain" description="Connect your domain or subdomain to this store.">
          <form onSubmit={handleAdd} className="space-y-4">
            <DomainSetupGuide />
            <Field id="domain-hostname" label="Domain name" hint="For example, shop.mybrand.com or mybrand.com.">
              <Input
                id="domain-hostname"
                type="text"
                placeholder="shop.example.com"
                value={hostname}
                onChange={(e) => setHostname(e.target.value)}
                autoFocus
                required
              />
            </Field>

            <label className="flex items-start gap-2.5 text-xs text-foreground cursor-pointer">
              <Checkbox
                checked={prevalidateTxt || typedApex}
                disabled={typedApex}
                onCheckedChange={(checked) => setPrevalidateTxt(Boolean(checked))}
                className="mt-0.5"
              />
              <span>
                <span className="font-medium block">Pre-validate ownership with a TXT record</span>
                <span className="text-muted-foreground block">
                  {typedApex
                    ? "Required for a bare domain: you prove ownership first, and only then point the domain to us."
                    : "Verify domain ownership with your DNS provider before routing traffic (recommended for live stores switching hosts)."}
                </span>
              </span>
            </label>

            <div className="flex gap-2">
              <Button type="submit" disabled={addDomain.isPending || !hostname.trim()}>
                {addDomain.isPending ? "Adding…" : "Add domain"}
              </Button>
              <Button type="button" variant="outline" onClick={() => setIsAdding(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </SettingsCard>
      )}

      {domains.length === 0 && !isAdding ? (
        <SettingsCard>
          <DomainSetupGuide />
          <EmptyState
            icon={Globe}
            title="No custom domains connected"
            description="Give your store a memorable, branded web address. You can connect a custom domain or subdomain that you own."
            action={
              <Button onClick={() => setIsAdding(true)}>
                <Plus className="mr-1.5 size-4" aria-hidden />
                Add domain
              </Button>
            }
          />
        </SettingsCard>
      ) : (
        domains.map((d) => {
          const isProviderDisabled = d.status === "requested" && d.sslStatus === "not_configured";
          const isActive = d.status === "active";
          // A domain saved before the platform finished its domain setup is still shown with its records; "Check status" registers it.
          const isDegraded = d.status === "degraded";
          const isPending = !isActive && d.status !== "failed" && !isDegraded;
          const showRecords = isPending || isDegraded;
          const apex = isApexDomain(d.hostname);
          const expected = d.routing?.expected;
          const cnameTarget = expected?.cname || d.verification?.cname || "stores.bcom.si";
          // A bare domain's A records resolve to Cloudflare, which then refuses to verify ownership by TXT. So the
          // records are only shown once ownership is verified; if they already exist, the merchant must remove them for now.
          const ownershipPending = apex && !isActive && !isDegraded && d.verification?.ownershipVerified === false;
          const seenIps = [...(d.routing?.seen.a ?? []), ...(d.routing?.seen.aaaa ?? [])];
          const routingNote = ownershipPending
            ? seenIps.length > 0
              ? {
                  tone: "problem" as const,
                  text: `Remove the A and AAAA records for ${d.hostname} (${seenIps.join(", ")}) for now. They stop the ownership check from finishing; you add them back in step 2.`,
                }
              : { tone: "wait" as const, text: "Waiting for ownership to be verified. Press Check status a minute after adding the TXT records." }
            : describeRouting(d.hostname, d.routing);
          const toneClass =
            routingNote.tone === "ok" ? "text-emerald-600 dark:text-emerald-400" : routingNote.tone === "problem" ? "text-destructive" : "text-amber-700 dark:text-amber-300";
          const isFailed = d.status === "failed";
          const isVerifying = verifyingId === d.id || verifyDomain.isPending;

          return (
            <SettingsCard key={d.id}>
              <div className="flex flex-col gap-4">
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border pb-3">
                  <div className="flex items-center gap-2.5">
                    <Globe className="size-4 text-muted-foreground" aria-hidden />
                    <span className="text-sm font-semibold text-foreground">{d.hostname}</span>
                    {d.isPrimary && (
                      <span className="rounded bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                        Primary
                      </span>
                    )}
                    {isActive && (
                      <span className="inline-flex items-center gap-1 rounded bg-emerald-500/10 px-2 py-0.5 text-xs font-medium text-emerald-600 dark:text-emerald-400">
                        <CheckCircle2 className="size-3" aria-hidden />
                        Active
                      </span>
                    )}
                    {d.sslStatus === "active" && (
                      <span className="inline-flex items-center gap-1 rounded bg-emerald-500/10 px-2 py-0.5 text-xs font-medium text-emerald-600 dark:text-emerald-400">
                        <ShieldCheck className="size-3" aria-hidden />
                        SSL active
                      </span>
                    )}
                    {isPending && (
                      <span className="inline-flex items-center gap-1 rounded bg-amber-500/10 px-2 py-0.5 text-xs font-medium text-amber-600 dark:text-amber-400">
                        <Clock className="size-3" aria-hidden />
                        Awaiting DNS
                      </span>
                    )}
                    {isDegraded && (
                      <span className="inline-flex items-center gap-1 rounded bg-destructive/10 px-2 py-0.5 text-xs font-medium text-destructive">
                        <AlertTriangle className="size-3" aria-hidden />
                        Needs attention
                      </span>
                    )}
                    {isFailed && (
                      <span className="inline-flex items-center gap-1 rounded bg-destructive/10 px-2 py-0.5 text-xs font-medium text-destructive">
                        <AlertTriangle className="size-3" aria-hidden />
                        Verification failed
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    {showRecords && (
                      <Button size="sm" variant="outline" disabled={isVerifying} onClick={() => handleVerify(d.id)}>
                        <RefreshCw className={`mr-1.5 size-3.5 ${isVerifying ? "animate-spin" : ""}`} aria-hidden />
                        {isVerifying ? "Checking…" : "Check status"}
                      </Button>
                    )}
                    {!d.isPrimary && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={!isActive || setPrimary.isPending}
                        onClick={() => handleSetPrimary(d.id)}
                        title={!isActive ? "Domain must be active before it can be set as primary" : undefined}
                      >
                        Make primary
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-destructive hover:bg-destructive/10"
                      onClick={() => setDomainToRemove(d)}
                    >
                      <Trash2 className="size-3.5" aria-hidden />
                      <span className="sr-only">Remove {d.hostname}</span>
                    </Button>
                  </div>
                </div>

                {isProviderDisabled && (
                  <div role="status" className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3.5 text-xs text-foreground">
                    <p className="font-medium text-amber-800 dark:text-amber-200">Waiting for platform setup</p>
                    <p className="mt-1 text-muted-foreground">
                      We have not registered this domain yet because custom domain provisioning is not switched on for this platform. You can create the DNS
                      record below now. Once it is switched on, press Check status and we will finish connecting it. Your store stays available on its platform
                      address in the meantime.
                    </p>
                  </div>
                )}

                {isDegraded && (
                  <div role="alert" className="rounded-md border border-destructive/30 bg-destructive/5 p-3.5 text-xs text-foreground">
                    <p className="font-medium text-destructive">This domain no longer reaches your store</p>
                    <p className="mt-1 text-muted-foreground">
                      Its DNS record changed or was removed, so visitors may not be able to open it. Re-create the record below, then press Check status.
                      Your store stays available on its platform address.
                    </p>
                  </div>
                )}

                {isFailed && (
                  <div role="alert" className="rounded-md border border-destructive/30 bg-destructive/5 p-3.5 text-xs text-foreground space-y-2">
                    <p className="font-medium text-destructive">DNS verification could not be completed</p>
                    <p className="text-muted-foreground">
                      DNS records could not be verified. Ensure your CNAME and TXT records are published with your DNS provider, then try again.
                    </p>
                    <div className="pt-1">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={isVerifying}
                        onClick={() => handleVerify(d.id)}
                      >
                        <RefreshCw className={`mr-1.5 size-3.5 ${isVerifying ? "animate-spin" : ""}`} aria-hidden />
                        {isVerifying ? "Checking…" : "Check status"}
                      </Button>
                      <p className="mt-1.5 text-[11px] text-muted-foreground">
                        DNS updates can take up to 24-48 hours to propagate worldwide. Avoid rapid retries to respect provider rate limits.
                      </p>
                    </div>
                  </div>
                )}

                {showRecords && (
                  <div className="space-y-3">
                    <div className="rounded-md border border-border bg-muted/40 p-3.5 text-xs">
                      <p className="font-medium text-foreground">DNS Configuration Required</p>
                      <p className="mt-0.5 text-muted-foreground">
                        Sign in to your domain registrar or DNS host (e.g. Cloudflare, GoDaddy, Hostinger, Namecheap) and add the following records:
                      </p>

                      <div className="mt-3 space-y-3 font-mono text-[11px]">
                        {d.prevalidateTxt && (
                          <div className="space-y-2">
                            <p className="font-sans text-xs font-medium text-foreground">Step 1: prove you own the domain (TXT)</p>
                            {d.verification?.txt ? (
                              <RecordLine
                                row={{ type: "TXT", name: d.verification.txt.name, value: d.verification.txt.value, copyLabel: "TXT verification value", copyButton: "Copy value" }}
                                onCopy={copy}
                              />
                            ) : null}
                            {d.verification?.dcv ? (
                              <RecordLine
                                row={{ type: "TXT", name: d.verification.dcv.name, value: d.verification.dcv.value, copyLabel: "certificate TXT value", copyButton: "Copy value" }}
                                onCopy={copy}
                              />
                            ) : (
                              <p className="font-sans text-muted-foreground">The certificate record appears here shortly. Press Check status in a minute.</p>
                            )}
                          </div>
                        )}

                        <div className="space-y-2">
                          <p className="font-sans text-xs font-medium text-foreground">
                            {d.prevalidateTxt ? `Step 2: point your domain to us (required)${ownershipPending ? " - not yet" : ""}` : "Point your domain to us"}
                          </p>
                          {ownershipPending ? (
                            <p className="font-sans text-muted-foreground">
                              Step 2 unlocks when step 1 is verified. Keep only the TXT records above for now, then press Check status.
                            </p>
                          ) : apex ? (
                            (expected?.a ?? []).length > 0 ? (
                              <>
                                {(expected?.a ?? []).map((ip) => (
                                  <RecordLine key={ip} row={{ type: "A", name: d.hostname, value: ip, copyLabel: "A record address", copyButton: "Copy address" }} onCopy={copy} />
                                ))}
                                {(expected?.aaaa ?? []).map((ip) => (
                                  <RecordLine key={ip} row={{ type: "AAAA", name: d.hostname, value: ip, copyLabel: "AAAA record address", copyButton: "Copy address" }} onCopy={copy} />
                                ))}
                                <p className="font-sans text-muted-foreground">
                                  Add every A record above (they all point to us); AAAA records are optional. If your provider offers ALIAS, ANAME or CNAME flattening,
                                  you can instead point <span className="font-mono">{d.hostname}</span> to <span className="font-mono">{cnameTarget}</span>. Or connect{" "}
                                  <span className="font-mono">www.{d.hostname}</span> and forward the bare domain to it.
                                </p>
                              </>
                            ) : (
                              <p className="font-sans text-muted-foreground">
                                We could not look up our addresses just now. Press Check status to try again, or connect <span className="font-mono">www.{d.hostname}</span>{" "}
                                with a CNAME to <span className="font-mono">{cnameTarget}</span>.
                              </p>
                            )
                          ) : (
                            <RecordLine
                              row={{ type: "CNAME", name: d.hostname, value: cnameTarget, copyLabel: "CNAME target", copyButton: "Copy target" }}
                              onCopy={copy}
                            />
                          )}
                          <p className={`font-sans ${toneClass}`} role="status">
                            {routingNote.text}
                          </p>
                        </div>
                      </div>

                      <div className="mt-4 flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-t border-border pt-3">
                        <Button size="sm" disabled={isVerifying} onClick={() => handleVerify(d.id)}>
                          <RefreshCw className={`mr-1.5 size-3.5 ${isVerifying ? "animate-spin" : ""}`} aria-hidden />
                          {isVerifying ? "Verifying…" : "Check status"}
                        </Button>
                        <span className="text-[11px] text-muted-foreground">
                          DNS updates can take up to 24-48 hours to propagate worldwide.
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </SettingsCard>
          );
        })
      )}
    </SettingsPageFrame>
  );
}
